import {
  listallViewsinaproject,
  ListallViewsinaprojectResponse,
  getViewDetails,
  GetViewDetailsResponse,
  createView,
  CreateViewRequest,
  ViewDetailsResponse,
  updatePresentation,
  UpdatePresentationRequest,
  downloadUrl,
  DownloadUrlResponse,
  getProjectDetails,
  getAvailableRegions,
  listallProjects,
  ListallProjectsResponse,
  listallComments,
  ListallCommentsResponse,
  listAllSearchResults,
  ListAllSearchResultsResponse2,
  updateProject,
  ProjectDetailsResponse,
  listTagsinaproject,
  ListTagsinaprojectResponse,
  listallUsersinproject,
  ListProjectUsersResponse,
  createTag,
  CreateTagResponse,
  addobjectstoTag,
  AddobjectstoTagResponse,
  getFileDetails,
  getversionsofaFile,
  deleteView,
  getalignment,
  createAlignment,
  getUserDetails,
  UserDetailsResponse,
  listlinksforthegiventargetentity,
  ListlinksforthegiventargetentityResponse,
  deleteaobjectlink,
} from '../generated/index';
import { mapToDomainFileDetails } from '../mappers';
import { FileDetails } from '../models/file/file-details.types';
import { Client, Options } from '@hey-api/client-fetch';
import { useQuery, UseQueryResult, QueryClient } from '@tanstack/solid-query';
import { returnOrThrow, throwIfError } from '@trimble/utilities';
import type {
  ReservedAppName,
  SectionBox,
} from '../generated-safe-to-regenerate/types.gen';
import {
  TcFileFolderItem,
  TcFileFolderSnapshotPage,
  TcFileFolderSnapshotPageOptions,
  TcModelAlignment,
  TcObjectType,
  TcProject,
  TcRegion,
} from './tc-tcps-api-service.models';
import type { ReservedViewDataApiDto } from '../generated-safe-to-regenerate/types.gen';
import { getRegionalBaseUrl } from '../utils/get-regional-base-url';
import { withoutNullProjectUsers } from '../helpers/without-null-project-users';
import { getFilesAndFoldersSnapshotPage } from './files-and-folders-snapshot';

export class TcTcpsApiService {
  // eslint-disable-next-line no-magic-numbers
  private static readonly HTTP_NOT_FOUND = 404;

  constructor(
    private readonly client: Client,
    private readonly queryClient: QueryClient
  ) {}

  private regionalOptions(
    region: TcRegion
  ): Pick<Options, 'client' | 'baseUrl'> {
    return {
      client: this.client,
      baseUrl: getRegionalBaseUrl(region),
    };
  }

  public getRegions(): UseQueryResult<TcRegion[], Error> {
    return useQuery(() => ({
      queryKey: ['tc-tcps', 'regions'],
      queryFn: async () => {
        const response = await getAvailableRegions({ client: this.client });

        return returnOrThrow(response, 'Error while listing regions');
      },
    }));
  }

  public getCurrentUser(): UseQueryResult<UserDetailsResponse, Error> {
    return useQuery(() => ({
      queryKey: ['tc-tcps', 'current-user'],
      queryFn: async () => {
        const response = await getUserDetails({
          path: { userId: 'me' },
          client: this.client,
        });

        return returnOrThrow(response, 'Error while getting current user');
      },
    }));
  }

  /**
   * Returns the URL to the project in Trimble Connect Web.
   * For prod: https://web.connect.trimble.com/projects/{projectId}/data/folder/
   * For non-prod: https://web.{environment}.connect.trimble.com/projects/{projectId}/data/folder/
   */
  public static getProjectUrl(
    environment: 'prod' | string,
    projectId: string
  ): string {
    const env = environment === 'prod' ? '' : `${environment}.`;
    return `https://web.${env}connect.trimble.com/projects/${projectId}/data/folder/`;
  }

  public async getRecentProjectsQuery(
    region: TcRegion,
    resultLimit: number,
    signal: AbortSignal
  ): Promise<ListallProjectsResponse> {
    const response = await listallProjects({
      headers: {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        Range: `items=0-${String(resultLimit - 1)}`,
      },
      query: {
        fullyLoaded: true,
        sort: '-lastVisited',
      },
      ...this.regionalOptions(region),
      signal,
    });

    return returnOrThrow(response, 'Error while listing recent projects');
  }

  public async searchProjects(
    region: TcRegion,
    resultLimit: number,
    searchTerm: string,
    signal: AbortSignal
  ): Promise<ListAllSearchResultsResponse2> {
    const response = await listAllSearchResults({
      headers: {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        Range: `items=0-${String(resultLimit - 1)}`,
      },
      query: {
        type: 'PROJECT',
        query: searchTerm,
        sort: '+name',
        fullyLoaded: false,
      },
      ...this.regionalOptions(region),
      signal,
    });

    return returnOrThrow(response, 'Error while searching projects');
  }

  public getImageBlobUrl(imageUrl: string): Promise<string> {
    return this.queryClient.fetchQuery({
      queryKey: ['tc-tcps', 'image-blob', imageUrl],
      queryFn: async () => {
        const url = new URL(imageUrl);
        const response = await this.client.request<Blob>({
          method: 'GET',
          url: `${url.pathname}${url.search}`,
          baseUrl: url.origin,
        });

        throwIfError(response, 'Failed to fetch image');

        return URL.createObjectURL(response.data!);
      },
    });
  }

  public listAllCommentsQuery(
    region: TcRegion,
    objectId: string,
    objectType: TcObjectType
  ): UseQueryResult<ListallCommentsResponse, Error> {
    return useQuery(() => ({
      queryKey: ['tc-tcps', 'comments', objectId, objectType, region],
      queryFn: async () => {
        const response = await listallComments({
          query: {
            objectId,
            objectType: objectType.toString(),
          },
          ...this.regionalOptions(region),
        });

        return returnOrThrow(response, 'Error while listing comments');
      },
      enabled: !!objectId && !!objectType,
    }));
  }

  private async fetchTags(
    region: TcRegion,
    projectId: string,
    objectId?: string,
    objectType?: TcObjectType
  ): Promise<ListTagsinaprojectResponse> {
    const response = await listTagsinaproject({
      query: {
        projectId,
        objectId,
        objectType: objectType?.toString(),
      },
      ...this.regionalOptions(region),
    });

    return returnOrThrow(response, 'Error while listing tags');
  }

  public listTagsQuery(
    region: TcRegion,
    projectId: string,
    objectId?: string,
    objectType?: TcObjectType
  ): UseQueryResult<ListTagsinaprojectResponse, Error> {
    return useQuery(() => ({
      queryKey: ['tc-tcps', 'tags', projectId, objectId, objectType, region],
      queryFn: () => this.fetchTags(region, projectId, objectId, objectType),
      enabled: !!projectId,
    }));
  }

  public listViewsQuery(
    region: TcRegion,
    projectId: string
  ): UseQueryResult<ListallViewsinaprojectResponse, Error> {
    return useQuery(() => ({
      queryKey: ['tc-tcps', 'views', projectId, region],
      queryFn: async () => {
        const response = await listallViewsinaproject({
          query: {
            projectId: projectId,
          },
          ...this.regionalOptions(region),
        });

        return returnOrThrow(response, 'Error while listing views');
      },
    }));
  }

  public listProjectUsersQuery(
    region: TcRegion,
    projectId: string
  ): UseQueryResult<ListProjectUsersResponse[], Error> {
    return useQuery(() => ({
      queryKey: ['tc-tcps', 'project-users', projectId, region],
      queryFn: async () => {
        const response = await listallUsersinproject({
          path: {
            projectId: projectId,
          },
          ...this.regionalOptions(region),
        });

        return withoutNullProjectUsers(
          returnOrThrow(response, 'Error while listing project users')
        );
      },
    }));
  }

  public async getViewDetails(
    viewId: string,
    region: TcRegion
  ): Promise<GetViewDetailsResponse> {
    const response = await getViewDetails({
      path: { viewId },
      ...this.regionalOptions(region),
    });

    return returnOrThrow(response, 'Error while getting view details');
  }

  public async createView(
    region: TcRegion,
    // sectionBox is added via the regenerated types; the primary ./generated
    // CreateViewRequest does not include it yet (see ./index.ts migration TODO).
    viewData: CreateViewRequest & { sectionBox?: SectionBox }
  ): Promise<ViewDetailsResponse> {
    const response = await createView({
      body: viewData,
      ...this.regionalOptions(region),
    });

    const result = returnOrThrow(response, 'Error while creating view');

    if (viewData.projectId) {
      await this.queryClient.invalidateQueries({
        queryKey: ['tc-tcps', 'views', viewData.projectId],
      });
    }

    return result;
  }

  public async updatePresentationForView(
    region: TcRegion,
    viewId: string,
    presentationData: UpdatePresentationRequest
  ): Promise<void> {
    const response = await updatePresentation({
      path: { viewId },
      body: presentationData,
      ...this.regionalOptions(region),
    });

    returnOrThrow(response, 'Error while updating presentation for view');
  }

  public getFileDetailsQuery(
    region: TcRegion,
    fileId: string
  ): UseQueryResult<FileDetails, Error> {
    return useQuery(() => ({
      queryKey: ['tc-tcps', 'file-details', fileId, region],
      queryFn: async () => {
        const response = await getFileDetails({
          path: { fileId },
          query: { tokenThumburl: false },
          ...this.regionalOptions(region),
        });

        throwIfError(response, 'Error while listing files and folders');

        if (response.data === undefined) return;

        return mapToDomainFileDetails(response.data);
      },
      enabled: !!fileId,
    }));
  }

  public getFileAlignmentQuery(
    region: TcRegion,
    fileId: string,
    versionId: string
  ): UseQueryResult<TcModelAlignment, Error> {
    return useQuery(() => ({
      queryKey: ['tc-tcps', 'file-alignment', fileId, versionId, region],
      queryFn: () => this.getFileAlignment(region, fileId, versionId),
      enabled: !!fileId && !!versionId,
      staleTime: 0,
      gcTime: 0,
    }));
  }

  public async getFileAlignment(
    region: TcRegion,
    fileId: string,
    versionId: string
  ): Promise<TcModelAlignment> {
    const response = await getalignment({
      path: { fileId },
      query: { versionId },
      ...this.regionalOptions(region),
    });

    if (
      response.error &&
      response.response?.status === TcTcpsApiService.HTTP_NOT_FOUND
    ) {
      // Return default alignment
      return {
        fileId,
        versionId,
        locationX: 0,
        locationY: 0,
        locationZ: 0,
        axisX: 0,
        axisY: 0,
        axisZ: 1,
        refDirectionX: 1,
        refDirectionY: 0,
        refDirectionZ: 0,
        scale: 1,
      };
    }

    return returnOrThrow(response, 'Error while getting alignment');
  }

  public async saveFileAlignment(
    region: TcRegion,
    alignment: TcModelAlignment
  ): Promise<TcModelAlignment> {
    const response = await createAlignment({
      path: { fileId: alignment.fileId },
      query: { versionId: alignment.versionId },
      body: {
        locationX: alignment.locationX,
        locationY: alignment.locationY,
        locationZ: alignment.locationZ,
        axisX: alignment.axisX,
        axisY: alignment.axisY,
        axisZ: alignment.axisZ,
        refDirectionX: alignment.refDirectionX,
        refDirectionY: alignment.refDirectionY,
        refDirectionZ: alignment.refDirectionZ,
        scale: alignment.scale,
      },
      ...this.regionalOptions(region),
    });

    const result = returnOrThrow(response, 'Error while saving alignment');

    await this.queryClient.invalidateQueries({
      queryKey: [
        'tc-tcps',
        'file-alignment',
        alignment.fileId,
        alignment.versionId,
        region,
      ],
    });

    return result;
  }

  public getFileVersionsQuery(
    region: TcRegion,
    fileId: string
  ): UseQueryResult<FileDetails[], Error> {
    return useQuery(() => ({
      queryKey: ['tc-tcps', 'file-versions', fileId, region],
      queryFn: async () => {
        const response = await getversionsofaFile({
          path: { fileId },
          query: { tokenThumburl: false },
          ...this.regionalOptions(region),
        });

        throwIfError(response, 'Error while listing files and folders');

        if (response.data === undefined) return;

        return response.data.map(mapToDomainFileDetails);
      },
      enabled: !!fileId,
    }));
  }

  public listFilesAndFoldersQuery(
    region: TcRegion,
    projectId: string
  ): UseQueryResult<TcFileFolderItem[], Error> {
    return useQuery(() => ({
      queryKey: ['tc-tcps', 'files-and-folders', projectId, region],
      queryFn: async ({ signal }) => {
        const projectDetails = await this.getProjectDetails(
          region,
          projectId,
          signal
        );
        const page = await this.getFilesAndFoldersSnapshotPage(
          region,
          projectId,
          projectDetails.rootId,
          {
            // Keep the existing query's one-page behavior for compatibility.
            // ProjectDataRepository follows the opaque `next` link when requested.
            maxItems: 100_000,
            signal,
          }
        );
        return [...page.items];
      },
    }));
  }

  public async getFilesAndFoldersSnapshotPage(
    region: TcRegion,
    projectId: string,
    rootId: string,
    options: TcFileFolderSnapshotPageOptions
  ): Promise<TcFileFolderSnapshotPage> {
    return getFilesAndFoldersSnapshotPage(
      this.client,
      region,
      projectId,
      rootId,
      options
    );
  }

  public getProjectDetails(
    region: TcRegion,
    projectId: string,
    signal?: AbortSignal
  ): Promise<TcProject> {
    return this.queryClient.fetchQuery({
      // eslint-disable-next-line @tanstack/query/exhaustive-deps -- signal is not included in dependencies on purpose, as we don't want to refetch project details when signal changes
      queryKey: ['tc-tcps', 'project-details', projectId, region],
      queryFn: async () => {
        const response = await getProjectDetails({
          path: { projectId },
          ...this.regionalOptions(region),
          signal,
        });

        return returnOrThrow(response, 'Error while getting project details');
      },
    });
  }

  public async updateProjectLastVisitedOn(
    region: TcRegion,
    projectId: string,
    lastVisitedOn: Date
  ): Promise<ProjectDetailsResponse> {
    const response = await updateProject({
      body: {
        lastVisitedOn: lastVisitedOn.toISOString(),
      },
      path: {
        projectId,
      },
      ...this.regionalOptions(region),
    });

    const result = returnOrThrow(response, 'Error while searching projects');

    await this.queryClient.invalidateQueries({
      queryKey: ['tc-tcps', 'project-details', projectId],
    });

    return result;
  }

  public getTrimBimDisplayModelData(
    region: TcRegion,
    modelId: string,
    versionId: string,
    signal: AbortSignal
  ): Promise<ArrayBuffer> {
    return this.queryClient.fetchQuery({
      queryKey: ['tc-tcps', 'file-download-data', modelId, versionId, region],
      meta: { signal },
      queryFn: async ({ signal: querySignal, meta }) => {
        const combinedSignal = AbortSignal.any([
          querySignal,
          meta!['signal'] as AbortSignal,
        ]);
        const fileDownloadUrl = await this.getFileDownloadUrl(
          region,
          modelId,
          versionId,
          'trb'
        );
        const response = await fetch(fileDownloadUrl.url, {
          signal: combinedSignal,
        });

        if (!response.ok) {
          throw new Error(
            `Error while getting model data: ${response.statusText}`
          );
        }

        return response.arrayBuffer();
      },
    });
  }

  public getFileDownloadUrl(
    region: TcRegion,
    fileId: string,
    versionId: string,
    format?: string
  ): Promise<DownloadUrlResponse> {
    return this.queryClient.fetchQuery({
      queryKey: [
        'tc-tcps',
        'file-download-url',
        fileId,
        versionId,
        format,
        region,
      ],
      queryFn: async () => {
        const response = await downloadUrl({
          path: { fileId },
          query: { versionId, format },
          ...this.regionalOptions(region),
        });

        return returnOrThrow(response, 'Error while getting download URL');
      },
    });
  }

  public async createTag(
    region: TcRegion,
    projectId: string,
    label: string,
    description?: string
  ): Promise<CreateTagResponse> {
    const response = await createTag({
      body: {
        label,
        description: description || '',
        projectId,
      },
      ...this.regionalOptions(region),
    });

    const result = returnOrThrow(response, 'Error while creating tag');

    await this.queryClient.invalidateQueries({
      queryKey: ['tc-tcps', 'tags', projectId],
    });

    return result;
  }

  public async associateTagsToObject(
    region: TcRegion,
    tagId: string,
    objectId: string,
    objectType: 'VIEW' | 'FILE' | 'FOLDER' | 'TODO'
  ): Promise<AddobjectstoTagResponse> {
    const response = await addobjectstoTag({
      path: { tagId },
      body: [
        {
          id: objectId,
          objectType,
        },
      ],
      ...this.regionalOptions(region),
    });

    const result = returnOrThrow(
      response,
      'Error while associating tag to object'
    );

    await this.queryClient.invalidateQueries({
      queryKey: ['tc-tcps', 'tags'],
    });

    return result;
  }

  /**
   * Save tags for a view
   * Creates new tags if they don't exist, then associates them with the view
   */
  public async saveTagsForView(
    region: TcRegion,
    projectId: string,
    viewId: string,
    tagItems: Array<{ id?: string; label: string }>
  ): Promise<void> {
    if (tagItems.length === 0) {
      return;
    }

    const existingTags = await this.queryClient.fetchQuery({
      queryKey: ['tc-tcps', 'tags', projectId, undefined, undefined, region],
      queryFn: () => this.fetchTags(region, projectId),
    });

    for (const tagItem of tagItems) {
      let tagId = tagItem.id;

      if (!tagId) {
        const existingTag = existingTags.find(
          (t) => t.label?.toLowerCase() === tagItem.label.toLowerCase()
        );

        if (existingTag?.id) {
          tagId = existingTag.id;
        } else {
          const createdTag = await this.createTag(
            region,
            projectId,
            tagItem.label
          );
          tagId = createdTag.id;
        }
      }

      if (tagId) {
        await this.associateTagsToObject(region, tagId, viewId, 'VIEW');
      }
    }

    await this.queryClient.invalidateQueries({
      queryKey: ['tc-tcps', 'tags', projectId],
    });
  }

  public async deleteView(
    region: TcRegion,
    viewId: string,
    projectId: string,
    force?: boolean
  ): Promise<void> {
    const response = await deleteView({
      path: { viewId },
      query: force ? { force } : undefined,
      ...this.regionalOptions(region),
    });
    returnOrThrow(response, 'Error while deleting view');
    await this.queryClient.invalidateQueries({
      queryKey: ['tc-tcps', 'views', projectId],
    });
  }

  public async getReservedViewData(
    region: TcRegion,
    viewId: string,
    appName: ReservedAppName
  ): Promise<ReservedViewDataApiDto> {
    const { baseUrl } = this.regionalOptions(region);
    const response = await this.client.get<ReservedViewDataApiDto>({
      url: `/views/${encodeURIComponent(viewId)}/reserved`,
      query: { app: appName },
      baseUrl,
    });

    return returnOrThrow(response, 'Error while getting reserved view data');
  }

  public async putReservedViewData(
    region: TcRegion,
    viewId: string,
    appName: ReservedAppName,
    data: Record<string, unknown>
  ): Promise<ReservedViewDataApiDto> {
    const { baseUrl } = this.regionalOptions(region);
    const response = await this.client.put<ReservedViewDataApiDto>({
      url: `/views/${encodeURIComponent(viewId)}/reserved`,
      query: { app: appName },
      body: data,
      baseUrl,
    });

    return returnOrThrow(response, 'Error while saving reserved view data');
  }

  public async listLinksForTarget(
    region: TcRegion,
    targetId: string,
    targetType: string,
    projectId: string,
    contextId: string
  ): Promise<ListlinksforthegiventargetentityResponse> {
    const response = await listlinksforthegiventargetentity({
      query: {
        id: targetId,
        type: targetType,
        projectId,
        includeDeleted: false,
        contextId,
      } as any,
      ...this.regionalOptions(region),
    });

    return returnOrThrow(response, 'Error while listing links for target');
  }

  public async deleteObjectLink(
    region: TcRegion,
    linkId: string
  ): Promise<void> {
    const response = await deleteaobjectlink({
      path: { linkId },
      ...this.regionalOptions(region),
    });

    returnOrThrow(response, 'Error while deleting object link');
  }

  public async deleteReservedViewData(
    region: TcRegion,
    viewId: string,
    appName: ReservedAppName
  ): Promise<void> {
    const { baseUrl } = this.regionalOptions(region);
    const response = await this.client.delete<void>({
      url: `/views/${encodeURIComponent(viewId)}/reserved`,
      query: { app: appName },
      baseUrl,
    });

    returnOrThrow(response, 'Error while deleting reserved view data');
  }
}
